<script lang="ts">
    import { useForm } from '@inertiajs/svelte';
    import { Input } from '@/components/ui/input';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    import InputError from '@/components/InputError.svelte';
    import { Toaster, toast } from 'svelte-sonner';
    import { onMount } from 'svelte';

    type Props = {
        recaptchaSiteKey: string | null;
        submitted?: boolean;
        errorMessage?: string | null;
    };

    let { recaptchaSiteKey, submitted = false, errorMessage = null }: Props = $props();

    const form = useForm({
        name: '',
        email: '',
        phone: '',
        message: '',
        gdpr_consent: false,
        recaptcha_token: '',
    });

    let recaptchaLoaded = $state(false);
    let showSuccess = $state(false);

    onMount(() => {
        if (recaptchaSiteKey) {
            const script = document.createElement('script');
            script.src =
                'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);

            (window as any).onRecaptchaLoad = () => {
                recaptchaLoaded = true;
                renderRecaptcha();
            };
        }
    });

    // The submission flow is stateless (the server returns this page with `submitted: true`
    // rather than a session redirect) so it works inside cross-origin iframes. React to that
    // prop to show success and notify the parent page for Google Tag Manager.
    let hasNotified = false;
    $effect(() => {
        if (submitted && !hasNotified) {
            hasNotified = true;
            showSuccess = true;
            notifyParentOfSubmission();
        }
    });

    $effect(() => {
        if (errorMessage) {
            toast.error(errorMessage);
        }
    });

    function renderRecaptcha() {
        if (!recaptchaLoaded || !recaptchaSiteKey) return;

        const container = document.getElementById('recaptcha-container');
        if (!container) return;

        (window as any).grecaptcha.render(container, {
            sitekey: recaptchaSiteKey,
            callback: (token: string) => {
                $form.recaptcha_token = token;
            },
            'expired-callback': () => {
                $form.recaptcha_token = '';
            },
        });
    }

    // Notify the parent page (when embedded in a cross-origin iframe) that a lead was
    // submitted, so its Google Tag Manager dataLayer can fire a conversion. The browser's
    // same-origin policy prevents GTM from observing events inside the iframe directly.
    function notifyParentOfSubmission() {
        if (window.parent !== window) {
            window.parent.postMessage(
                { event: 'culmex_iframe_form_submit', formName: 'short_quote_form' },
                '*'
            );
        }
    }

    // Validate required fields client-side and surface inline errors. Server validation
    // errors are session-backed and so never reach a cross-origin iframe, so this is the
    // only feedback the user gets for missing fields. The server still validates as the
    // authoritative backstop.
    const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    function validateForm(): boolean {
        $form.clearErrors();
        let valid = true;

        if (!$form.name.trim()) {
            $form.setError('name', 'Please enter your name.');
            valid = false;
        }

        if (!$form.email.trim()) {
            $form.setError('email', 'Please enter your email address.');
            valid = false;
        } else if (!EMAIL_PATTERN.test($form.email)) {
            $form.setError('email', 'Please enter a valid email address.');
            valid = false;
        }

        if (!$form.phone.trim()) {
            $form.setError('phone', 'Please enter your phone number.');
            valid = false;
        }

        return valid;
    }

    // Scroll to and focus the first field with a validation error. Field ids match the
    // form keys, and this order follows the visual order of the fields.
    function focusFirstError(): void {
        const order = ['name', 'email', 'phone'];
        const errors = $form.errors as Record<string, string>;
        const firstField = order.find((field) => errors[field]);
        if (!firstField) {
            return;
        }
        const el = document.getElementById(firstField);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus({ preventScroll: true });
    }

    function submit(e: Event) {
        e.preventDefault();

        if (!validateForm()) {
            focusFirstError();
            return;
        }

        if (!$form.gdpr_consent) {
            toast.error('You must consent to data processing to submit this form');
            return;
        }

        if (!$form.recaptcha_token && recaptchaSiteKey) {
            toast.error('Please complete the reCAPTCHA verification');
            return;
        }

        // Success is handled via the `submitted` prop returned by the server (see the
        // $effect above), which keeps the flow stateless for cross-origin iframe embedding.
        $form.post(route('embed.contact-form-short.store'), {
            onError: (errors) => {
                if (errors.general) {
                    toast.error(errors.general);
                }
            },
        });
    }

    function submitAnother() {
        // Reload to a fresh form so a second submission re-triggers the success state.
        window.location.reload();
    }
</script>

<svelte:head>
    <title>Get a Quote - Culmex Scaffolding</title>
    <link
        href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap"
        rel="stylesheet"
    />
</svelte:head>

<Toaster position="top-right" richColors closeButton />

<div class="culmex-embed">
    <div class="culmex-container">
        {#if showSuccess}
            <div class="culmex-card">
                <div class="culmex-success">
                    <svg
                        class="culmex-success-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                    <h2 class="culmex-success-title">Thank You!</h2>
                    <p class="culmex-success-text">
                        Your enquiry has been submitted successfully. We will be in touch soon.
                    </p>
                    <button class="culmex-btn-outline" onclick={submitAnother}>
                        Submit Another Enquiry
                    </button>
                </div>
            </div>
        {:else}
            <form onsubmit={submit} class="culmex-form">
                <div class="culmex-card">
                    <div class="culmex-card-body">
                        <h1 class="culmex-title">GET A QUOTE NOW</h1>

                        <div class="culmex-field">
                            <label class="culmex-label" for="name">
                                Name <span class="culmex-required">*</span>
                            </label>
                            <Input
                                id="name"
                                type="text"
                                bind:value={$form.name}
                                maxlength={255}
                                class="culmex-input"
                            />
                            <InputError message={$form.errors.name} />
                        </div>

                        <div class="culmex-field">
                            <label class="culmex-label" for="email">
                                Email <span class="culmex-required">*</span>
                            </label>
                            <Input
                                id="email"
                                type="email"
                                bind:value={$form.email}
                                class="culmex-input"
                            />
                            <InputError message={$form.errors.email} />
                        </div>

                        <div class="culmex-field">
                            <label class="culmex-label" for="phone">
                                Phone <span class="culmex-required">*</span>
                            </label>
                            <Input
                                id="phone"
                                type="tel"
                                bind:value={$form.phone}
                                class="culmex-input"
                            />
                            <InputError message={$form.errors.phone} />
                        </div>

                        <Textarea
                            label="Message"
                            bind:value={$form.message}
                            maxlength={2000}
                            error={$form.errors.message}
                        />

                        <div class="culmex-field">
                            <div class="culmex-consent-row">
                                <input
                                    type="checkbox"
                                    id="gdpr_consent"
                                    bind:checked={$form.gdpr_consent}
                                    class="culmex-native-checkbox"
                                />
                                <div class="culmex-consent-text">
                                    <label for="gdpr_consent" class="culmex-consent-title">
                                        I consent to Culmex Scaffolding storing and processing my
                                        information.
                                        <span class="culmex-required">*</span>
                                    </label>
                                    <p class="culmex-hint">
                                        By submitting this form you consent to us storing and
                                        processing your personal data according to our
                                        <a
                                            href="https://culmexconstruction.co.uk/privacy-policy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="culmex-link">Privacy Policy</a
                                        >.
                                    </p>
                                </div>
                            </div>
                            <InputError message={$form.errors.gdpr_consent} />
                        </div>

                        {#if recaptchaSiteKey}
                            <div class="culmex-field">
                                <div id="recaptcha-container"></div>
                                <InputError message={$form.errors.recaptcha_token} />
                            </div>
                        {/if}

                        <div class="culmex-submit-row">
                            <button
                                type="submit"
                                class="culmex-btn-primary"
                                disabled={$form.processing}
                            >
                                {$form.processing ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        {/if}
    </div>
</div>

<style>
    .culmex-embed {
        font-family: 'Open Sans', sans-serif;
        background: #f5f5f5;
        min-height: 100vh;
        padding: 2rem 1rem;
        color: #333;
    }

    .culmex-container {
        max-width: 480px;
        margin: 0 auto;
    }

    .culmex-card {
        background: #fff;
        border: 1px solid #ddd;
    }

    .culmex-card-body {
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .culmex-title {
        font-size: 1.4rem;
        font-weight: 700;
        color: #1e3060;
        margin: 0 0 0.5rem;
        letter-spacing: 0.02em;
    }

    .culmex-field {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
    }

    .culmex-label {
        font-size: 0.85rem;
        font-weight: 600;
        color: #1e3060;
        letter-spacing: 0.01em;
    }

    .culmex-required {
        color: #c0392b;
    }

    .culmex-hint {
        font-size: 0.8rem;
        color: #777;
        margin: 0;
    }

    .culmex-native-checkbox {
        width: 1rem;
        height: 1rem;
        margin-top: 0.15rem;
        flex-shrink: 0;
        accent-color: #1e3060;
        cursor: pointer;
    }

    .culmex-consent-row {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
    }

    .culmex-consent-text {
        flex: 1;
        font-size: 0.875rem;
    }

    .culmex-consent-title {
        font-weight: 600;
        color: #1e3060;
    }

    .culmex-link {
        color: #1e3060;
        text-decoration: underline;
    }

    .culmex-link:hover {
        text-decoration: none;
    }

    .culmex-btn-primary {
        display: inline-block;
        padding: 0.6rem 2rem;
        background: #1e3060;
        color: #fff;
        font-family: inherit;
        font-size: 0.85rem;
        font-weight: 600;
        border: 2px solid #1e3060;
        border-radius: 4px;
        cursor: pointer;
        transition:
            background 0.15s,
            color 0.15s;
    }

    .culmex-btn-primary:hover:not(:disabled) {
        background: #fff;
        color: #1e3060;
    }

    .culmex-btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .culmex-btn-outline {
        display: inline-block;
        padding: 0.7rem 2.5rem;
        background: #fff;
        color: #1e3060;
        font-family: inherit;
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        border: 2px solid #1e3060;
        cursor: pointer;
        transition:
            background 0.15s,
            color 0.15s;
    }

    .culmex-btn-outline:hover {
        background: #1e3060;
        color: #fff;
    }

    .culmex-submit-row {
        display: flex;
        justify-content: flex-end;
        padding-top: 0.5rem;
    }

    .culmex-success {
        padding: 3rem 2rem;
        text-align: center;
    }

    .culmex-success-icon {
        width: 4rem;
        height: 4rem;
        margin: 0 auto 1rem;
        color: #1e3060;
    }

    .culmex-success-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: #1e3060;
        margin: 0 0 0.75rem;
    }

    .culmex-success-text {
        color: #555;
        margin: 0 0 1.5rem;
    }
</style>
